<template>
  <div class="hover-card">
    <div class="hc-head">
      <img class="hc-portrait" :src="portrait(members, teamColor)" :alt="members.Class"
           @error="$event.target.style.visibility = 'hidden'">
      <div class="hc-id">
        <div class="hc-name" :style="nameStyle(members.Name)">
          <span v-if="members.RaidBoss" class="hc-boss">★ BOSS</span>{{ members.Name }}<span class="grade-badge" :class="'tier-' + grade.color" :title="grade.color + ' ' + grade.grade + ' — ' + members.Class + ' type wins ' + grade.typeWR + '%, this one ' + (grade.specimenLift >= 0 ? '+' : '') + grade.specimenLift">{{ grade.grade }}</span>
        </div>
        <div class="hc-class">{{ members.Class }} · {{ members.Sign }}</div>
        <div class="hc-bf"><span :style="wrS('Brave', members.Brave)">Brave {{ members.Brave }}</span> · <span :style="wrS('Faith', members.Faith)">Faith {{ members.Faith }}</span></div>
      </div>
    </div>

    <div v-if="enemyCompat.length || teamCompat.length" class="hc-compat">
      <div v-if="enemyCompat.length" class="hc-compat-block" :style="blockStyle(enemyColor)">
        <div class="hc-compat-label">vs {{ enemyColor }}</div>
        <div class="hc-compat-row">
          <span v-for="(c, i) in enemyCompat" :key="'e' + i" class="hc-compat-unit">
            <img :src="portrait(c.unit, enemyColor)" :alt="c.unit.Class" @error="$event.target.style.visibility = 'hidden'">
            <span :class="'z-' + c.compat.toLowerCase()">{{ c.compat }}</span>
          </span>
        </div>
      </div>
      <div v-if="teamCompat.length" class="hc-compat-block" :style="blockStyle(teamColor)">
        <div class="hc-compat-label">{{ teamColor }} team</div>
        <div class="hc-compat-row">
          <span v-for="(c, i) in teamCompat" :key="'t' + i" class="hc-compat-unit">
            <img :src="portrait(c.unit, teamColor)" :alt="c.unit.Class" @error="$event.target.style.visibility = 'hidden'">
            <span :class="'z-' + c.compat.toLowerCase()">{{ c.compat }}</span>
          </span>
        </div>
      </div>
    </div>

    <div class="hc-stats">
      <span :style="wrS('StatHP', stats.hp)">HP {{ stats.hp }}</span><span>MP {{ stats.mp }}</span>
      <span :style="wrS('StatPA', stats.pa)">PA {{ stats.pa }}</span><span :style="wrS('StatMA', stats.ma)">MA {{ stats.ma }}</span>
      <span :style="wrS('StatSpeed', stats.speed)">Sp {{ stats.speed }}</span>
      <span>Mv {{ stats.move }}</span><span>Jp {{ stats.jump }}</span>
    </div>

    <div class="hc-line hc-evade">
      Evade — F {{ evasion.front }}% · S {{ evasion.side }}% · B {{ evasion.back }}% · Magic {{ evasion.magic }}%
    </div>

    <template v-if="members.Gender == 'Monster' && monsterAttacks.length">
      <div class="hc-gearlabel">Attacks</div>
      <div class="hc-gear">
        <div v-for="(a, i) in monsterAttacks" :key="i" class="hc-gear-row">
          <span>{{ a.name }}<span v-if="a.dmg" class="hc-dmg"> ({{ a.dmg }})</span></span>
        </div>
      </div>
    </template>

    <template v-if="members.Gender != 'Monster'">
      <div class="hc-line hc-skills">
        <span v-if="members.ActionSkill"><b>Action:</b> <span :style="wr('ActionSkill', members.ActionSkill)" :title="wrTip('ActionSkill', members.ActionSkill)">{{ members.ActionSkill }}</span></span>
        <span v-if="members.ReactionSkill"><b>React:</b> <span :style="wr('ReactionSkill', members.ReactionSkill)" :title="wrTip('ReactionSkill', members.ReactionSkill)">{{ members.ReactionSkill }}</span></span>
        <span v-if="members.SupportSkill"><b>Support:</b> <span :style="wr('SupportSkill', members.SupportSkill)" :title="wrTip('SupportSkill', members.SupportSkill)">{{ members.SupportSkill }}</span></span>
        <span v-if="members.MoveSkill"><b>Move:</b> <span :style="wr('MoveSkill', members.MoveSkill)" :title="wrTip('MoveSkill', members.MoveSkill)">{{ members.MoveSkill }}</span></span>
      </div>

      <div v-if="notableSkills.length" class="hc-line hc-notable">
        <span v-for="(s, i) in notableSkills" :key="i" class="hc-notable-skill">
          <span class="hc-pill" :style="{ background: s.tier.pill }">{{ s.roll }}%</span><span :style="wr('Ability', s.name)" :title="wrTip('Ability', s.name)">{{ s.name }}</span>
        </span>
      </div>

      <div class="hc-gear">
        <div class="hc-gear-row">
          <img v-if="members.Mainhand" class="hc-gear-icon" :src="itemIcon(members.Mainhand)" :alt="members.Mainhand" @error="$event.target.style.visibility = 'hidden'">
          <span v-if="members.Mainhand" :style="wr('Weapon', members.Mainhand)" :title="wrTip('Weapon', members.Mainhand)">{{ members.Mainhand }}<span v-if="weaponDamage('Mainhand') !== null" class="hc-dmg"> ({{ weaponDamage('Mainhand') }})</span></span>
          <span v-else class="hc-unarmed">Unarmed <span class="hc-dmg">({{ unarmedDamage() }})</span></span>
        </div>
        <div v-if="members.Offhand" class="hc-gear-row">
          <img class="hc-gear-icon" :src="itemIcon(members.Offhand)" :alt="members.Offhand" @error="$event.target.style.visibility = 'hidden'">
          <span :style="wr('Weapon', members.Offhand)" :title="wrTip('Weapon', members.Offhand)">{{ members.Offhand }}<span v-if="weaponDamage('Offhand') !== null" class="hc-dmg"> ({{ weaponDamage('Offhand') }})</span></span>
        </div>
        <div v-if="members.Head" class="hc-gear-row"><img class="hc-gear-icon" :src="itemIcon(members.Head)" :alt="members.Head" @error="$event.target.style.visibility = 'hidden'"><span :style="wr('Head', members.Head)" :title="wrTip('Head', members.Head)">{{ members.Head }}</span></div>
        <div v-if="members.Armor" class="hc-gear-row"><img class="hc-gear-icon" :src="itemIcon(members.Armor)" :alt="members.Armor" @error="$event.target.style.visibility = 'hidden'"><span :style="wr('Armor', members.Armor)" :title="wrTip('Armor', members.Armor)">{{ members.Armor }}</span></div>
        <div v-if="members.Accessory" class="hc-gear-row"><img class="hc-gear-icon" :src="itemIcon(members.Accessory)" :alt="members.Accessory" @error="$event.target.style.visibility = 'hidden'"><span :style="wr('Accessory', members.Accessory)" :title="wrTip('Accessory', members.Accessory)">{{ members.Accessory }}</span></div>
      </div>
    </template>

    <div v-if="affinityText" class="hc-line hc-affinity">{{ affinityText }}</div>
    <div v-if="statusText" class="hc-line hc-status">{{ statusText }}</div>
  </div>
</template>

<script>
import { weaponDamage as calcWeaponDamage, unarmedDamage as calcUnarmedDamage } from '../lib/damage.js';
import { zodiacCompat } from '../lib/zodiac.js';
import { unitElementProfile } from '../lib/elements.js';
import { teamBright } from '../lib/teamColors.js';
import { computeAbilityDamage, parseAbilityInfo } from '../lib/abilityFormula.js';
import { hitChance } from '../lib/evasion.js';
import { skillRarity } from '../lib/skillRarity.js';
import skillRarityData from '../data/skillRarity.json';
import { rosterStats } from '../lib/preload.js';
import { unitGrade } from '../lib/unitGrade.js';
import { wrColor, wrTitle, wrStatColor, wrStatTitle, wrMonsterBFColor } from '../lib/winRateColor.js';
import { archetypeOf, sBucket, bfBucket } from '../lib/unitGrade.js';

import { UNITS_BASE as UNIT_IMG } from '../lib/assetUrls.js';
import { ICONS_BASE as ITEM_IMG } from '../lib/assetUrls.js';

export default {
  props: {
    members: {},
    itemschanged: {},
    charStats: {},
    notAbles: {},
    teamColor: '',
    enemyColor: '',
    allunits: {},
    abilitiesInfo: { type: Object, default: () => ({}) },
    toolTips: { type: Object, default: () => ({}) },
    // Map<lowercase-username, color> for custom highlighted users.
    highlightColors: { type: Object, default: () => new Map() },
    showWinRates: { type: Boolean, default: false },
  },

  computed: {
    grade() { return unitGrade(this.members, rosterStats, { charStats: this.charStats, itemschanged: this.itemschanged, notAbles: this.notAbles }); },
    classKey() {
      return (this.members.Class || '').replace(/\s+/g, '') + (this.members.Gender || '');
    },
    classBase() { return this.charStats[this.classKey]; },
    equipped() {
      return {
        Mainhand:  this.itemschanged[this.members.Mainhand],
        Offhand:   this.itemschanged[this.members.Offhand],
        Head:      this.itemschanged[this.members.Head],
        Armor:     this.itemschanged[this.members.Armor],
        Accessory: this.itemschanged[this.members.Accessory],
      };
    },
    itemBonus() {
      const e = this.equipped;
      const sum = (k) => e.Mainhand[k] + e.Offhand[k] + e.Head[k] + e.Armor[k] + e.Accessory[k];
      return { hp: sum('hp'), mp: sum('mp'), pa: sum('pa'), ma: sum('ma'), speed: sum('speed'), move: sum('move'), jump: sum('jump') };
    },
    itemBonusPAUnarmed() {
      const e = this.equipped;
      return e.Head.pa + e.Armor.pa + e.Accessory.pa;
    },
    moveSkillBonus() {
      return this.notAbles.movementSkills[this.members.MoveSkill];
    },
    // Only the unit's non-common action skills (own class + secondary set),
    // rarest first — the compact hover surfaces the highlights, not the full
    // skill list.
    notableSkills() {
      if (this.members.Gender === 'Monster') return [];
      const out = [];
      for (const a of this.members.ClassSkills || []) {
        const r = skillRarity(a, false, skillRarityData);
        if (r) out.push({ name: a, tier: r.tier, roll: r.roll });
      }
      for (const a of this.members.ExtraSkills || []) {
        const r = skillRarity(a, true, skillRarityData);
        if (r) out.push({ name: a, tier: r.tier, roll: r.roll });
      }
      return out.sort((x, y) => x.roll - y.roll);
    },
    stats() {
      const c = this.classBase, b = this.itemBonus, m = this.moveSkillBonus;
      return {
        hp: c.hp + b.hp, mp: c.mp + b.mp, pa: c.pa + b.pa, ma: c.ma + b.ma,
        speed: c.speed + b.speed, move: c.move + b.move + m.move, jump: c.jump + b.jump + m.jump,
      };
    },
    paUnarmed() {
      return this.classBase.pa + this.itemBonusPAUnarmed;
    },

    affinityText() {
      const p = unitElementProfile(this.equipped);
      const bits = [];
      if (p.strengthen.size) bits.push('Strengthen ' + [...p.strengthen].join('/'));
      if (p.absorb.size) bits.push('Absorb ' + [...p.absorb].join('/'));
      if (p.half.size) bits.push('Half ' + [...p.half].join('/'));
      if (p.weak.size) bits.push('Weak ' + [...p.weak].join('/'));
      return bits.join('  ·  ');
    },
    statusText() {
      const perm = [], init = [];
      for (const slot of Object.values(this.equipped)) {
        for (const s of (slot.permstatus || [])) perm.push(s.name);
        for (const s of (slot.initstatus || [])) init.push(s.name);
      }
      const bits = [];
      if (perm.length) bits.push('Perm: ' + [...new Set(perm)].join(', '));
      if (init.length) bits.push('Init: ' + [...new Set(init)].join(', '));
      return bits.join('  ·  ');
    },
    teamCompat() { return this.compatFor(this.teamColor, true); },
    enemyCompat() { return this.compatFor(this.enemyColor, false); },

    // This unit's evade % by direction (chance to dodge an incoming attack).
    evasion() {
      const target = { equipped: this.equipped, cEv: this.classBase.cEvPercent, reaction: this.members.ReactionSkill };
      const ev = (type, dir) => Math.round(100 - hitChance(target, { type, direction: dir }));
      return { front: ev('physical', 'front'), side: ev('physical', 'side'), back: ev('physical', 'back'), magic: ev('magical', 'front') };
    },

    // Monsters have no gear — their "attacks" are their monster skills.
    monsterAttacks() {
      if (this.members.Gender !== 'Monster') return [];
      const skills = (this.toolTips.MonsterSkills && this.toolTips.MonsterSkills[this.members.Class]) || [];
      const ctx = {
        ma: this.stats.ma, pa: this.stats.pa, speed: this.stats.speed,
        brave: this.members.Brave, faith: this.members.Faith, targetFaith: 60,
        wp: 0, maxHp: this.stats.hp, paUnarmed: this.paUnarmed,
      };
      return skills.map((raw) => {
        const name = (raw || '').replace('*', '');
        const model = parseAbilityInfo(this.abilitiesInfo[name]);
        let dmg = '';
        if (model && model.kind === 'number' && model.verb === 'Damage') {
          const { final } = computeAbilityDamage(model, ctx, {});
          dmg = Array.isArray(final) ? `${final[0]} - ${final[1]}` : `${final}`;
        } else if (model && model.kind === 'percent') {
          dmg = `${model.evalFn(ctx)}%`;
        }
        return { name, dmg };
      });
    },
  },

  methods: {
    wr(dim, value) { return this.showWinRates ? { color: wrColor(dim, value, this.members.Class, this.members.Gender) } : {}; },
    wrTip(dim, value) { return this.showWinRates ? wrTitle(dim, value, this.members.Class, this.members.Gender) : ''; },
    wrS(dim, raw) { if (!this.showWinRates) return {}; const isMonster = this.members.Gender === 'Monster'; if (isMonster && dim.startsWith('Stat')) return {}; if (isMonster && (dim === 'Brave' || dim === 'Faith')) return { color: wrMonsterBFColor(this.members.Class, dim, raw, this.grade.typeWR) }; const a = archetypeOf(this.classBase, isMonster); const b = dim === 'Brave' || dim === 'Faith' ? bfBucket(raw) : (sBucket[dim.replace('Stat', '')] || (() => String(raw)))(raw); const cg = isMonster ? null : (this.members.Class && this.members.Gender ? this.members.Class + ' ' + this.members.Gender : null); return { color: wrStatColor(dim, a, b, this.grade.typeWR, cg) }; },
    nameStyle(name) {
      const c = this.highlightColors && this.highlightColors.get((name || '').toLowerCase());
      return c ? { color: c, fontWeight: 'bold' } : null;
    },
    itemIcon(name) { return ITEM_IMG + (name || '').replace(/\s+/g, '') + '.gif'; },
    // Note: NOT named teamColor — that's a prop, and a same-named method
    // shadows it (broke {{ teamColor }} and portrait lookups).
    teamHex(label) { return teamBright(label); },
    blockStyle(label) {
      const c = this.teamHex(label);
      return { border: '2px solid ' + c, background: c + '2e' };
    },
    portrait(unit, label) {
      if (unit.Gender === 'Monster') return UNIT_IMG + unit.Class + '.gif';
      const g = (unit.Gender || 'Male')[0];
      if (label === 'Champion') return UNIT_IMG + unit.Class + g + '.gif';
      return UNIT_IMG + unit.Class + g + '_' + label + '.gif';
    },
    // Zodiac compatibility vs each unit on `label`'s team. `skipSelf` drops
    // this unit from its own team's list.
    compatFor(label, skipSelf) {
      if (!this.allunits || !label) return [];
      const team = this.allunits[label.toLowerCase()];
      if (!team || !team.Units) return [];
      return team.Units
        .filter((u) => !(skipSelf && u.Name === this.members.Name))
        .map((u) => ({ unit: u, compat: zodiacCompat(this.members.Gender, this.members.Sign, u.Gender, u.Sign) }));
    },
    weaponDamage(slot) {
      return calcWeaponDamage({
        item: this.equipped[slot],
        pa: this.stats.pa, ma: this.stats.ma, speed: this.stats.speed,
        brave: this.members.Brave, faith: this.members.Faith,
        doublehand: this.members.SupportSkill === 'Doublehand',
        targetFaith: 60,
        // Reflect the unit's attacker supports (matches the inline card / fight view).
        attackUp: this.members.SupportSkill === 'Attack Up',
        magicAttackUp: this.members.SupportSkill === 'Magic Attack Up',
        strengthen: unitElementProfile(this.equipped).strengthen,
      });
    },
    unarmedDamage() {
      return calcUnarmedDamage({
        paUnarmed: this.paUnarmed,
        brave: this.members.Brave,
        isMartialArts: this.members.SupportSkill === 'Martial Arts' || this.members.Class === 'Monk',
        attackUp: this.members.SupportSkill === 'Attack Up',
      });
    },
  },
};
</script>

<style scoped>
.hover-card {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 13px;
  line-height: 1.4;
  text-align: left;
  min-width: 230px;
}
.hc-head { display: flex; gap: 10px; align-items: center; margin-bottom: 6px; }
.hc-portrait { width: 56px; height: 56px; object-fit: contain; flex: 0 0 auto; }
.hc-name { font-size: 16px; font-weight: bold; line-height: 1.2; }
.hc-boss { color: gold; margin-right: 5px; font-size: 11px; }
.hc-class, .hc-bf { font-size: 12px; opacity: 0.9; }

.hc-stats {
  display: flex; flex-wrap: wrap; gap: 1px 12px;
  margin: 4px 0; padding: 4px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}
.hc-stats span { white-space: nowrap; font-size: 12px; }

.hc-line { font-size: 12px; margin: 4px 0; display: flex; flex-wrap: wrap; gap: 1px 12px; }
.hc-skills b, .hc-gear .hc-slot { font-weight: bold; }

.hc-gear { display: grid; grid-template-columns: 1fr 1fr; gap: 1px 14px; margin: 4px 0; font-size: 12px; }
.hc-gear-row { display: flex; gap: 6px; align-items: center; min-width: 0; }
.hc-gear-row > span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.hc-gear-icon { width: 18px; height: 18px; object-fit: contain; flex: 0 0 auto; }
.hc-unarmed { margin-left: 24px; }
.hc-gearlabel { font-size: 11px; opacity: 0.6; margin-top: 4px; }
.hc-dmg { opacity: 0.8; }
.hc-evade { color: #cbe6ff; font-size: 11px; }

.hc-affinity { color: #ffd27a; }
.hc-status { color: #9ad0ff; }

/* Rare-skill highlights — rarest first, tier-colored pill + name. */
.hc-notable { gap: 3px 10px; }
.hc-notable-skill { white-space: nowrap; font-size: 12px; }
.hc-pill {
  display: inline-block; padding: 0 5px; margin-right: 4px;
  border-radius: 8px; color: #fff; font-size: 0.72em; font-weight: bold; vertical-align: middle;
}

/* Compat: each team in its own colored block, label on top. */
.hc-compat { display: flex; flex-wrap: wrap; gap: 8px; margin: 6px 0; }
.hc-compat-block { border-radius: 6px; padding: 4px 6px; }
.hc-compat-label { font-size: 11px; opacity: 0.9; margin-bottom: 3px; text-align: center; }
.hc-compat-row { display: flex; flex-wrap: wrap; gap: 5px; justify-content: center; }
.hc-compat-unit { display: flex; flex-direction: column; align-items: center; width: 30px; }
.hc-compat-unit img { width: 24px; height: 24px; object-fit: contain; }
.hc-compat-unit span { font-size: 9px; line-height: 1.1; }
.z-good, .z-best { color: #4ee08a; }
.z-bad, .z-worst { color: #ff8a8a; }
.z-neutral { opacity: 0.45; }
</style>
